import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PdfColor,
  PdfPen,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import '../../pdf/draw/shapes';
import { moduleRegistry } from '../../modules';
import { clockModule } from '../../modules/clock';
import { textModule } from '../../modules/text';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);

export class TestClock implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(clockModule);
    moduleRegistry.register(textModule);

    const font = new PdfFont(PdfFont.FONT_NAME, 14);
    const smallFont = new PdfFont(PdfFont.FONT_NAME, 10);

    const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(8));

    const clock = moduleRegistry.get('clock');
    if (!clock) {
      throw new Error('Clock module not found');
    }

    const page1 = pdf.addPage();
    page1.drawString(
      'Clock Module Tests - Basic Clocks',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const rectOutline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);

    const testCases = [
      { time: '0:00', label: '1. Midnight (0:00)', size: undefined },
      { time: '6:00', label: '2. 6:00 AM', size: undefined },
      { time: '9:00', label: '3. 9:00 AM', size: undefined },
      { time: '12:00', label: '4. Noon (12:00)', size: undefined },
      { time: '15:30', label: '5. 3:30 PM', size: undefined },
      { time: '18:45', label: '6. 6:45 PM', size: undefined },
      { time: '23:59', label: '7. 23:59', size: undefined },
      { time: '2:30', label: '8. 2:30 AM', size: undefined },
    ];

    let y = MM(25);
    for (let i = 0; i < testCases.length; i++) {
      const tc = testCases[i];
      const rect = new PdfRect(MARGIN, y, MM(50), MM(30));
      page1.drawString(
        tc.label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + MM(55), y + MM(10), MM(100), MM(5)),
        ETextAlign.CenterLeft
      );
      page1.drawRectangle(rectOutline, null, rect);

      const content = clock.parse(tc.time, {});
      const measured = clock.measure(content, { font, page: page1 });
      clock.draw(content, rect, ETextAlign.Center, { font, page: page1 });

      y += MM(35);
    }

    const page2 = pdf.addPage();
    page2.drawString(
      'Clock Module Tests - Clocks with Seconds',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const testCasesSeconds = [
      { time: '0:00:00', label: '1. Midnight with seconds', size: undefined },
      { time: '12:30:45', label: '2. 12:30:45', size: undefined },
      { time: '15:20:00', label: '3. 15:20:00 (example)', size: undefined },
      { time: '23:59:59', label: '4. 23:59:59', size: undefined },
      { time: '6:00:30', label: '5. 6:00:30', size: undefined },
      { time: '9:15:07', label: '6. 9:15:07', size: undefined },
      { time: '14:45:15', label: '7. 14:45:15', size: undefined },
      { time: '21:00:00', label: '8. 21:00:00', size: undefined },
    ];

    y = MM(25);
    for (let i = 0; i < testCasesSeconds.length; i++) {
      const tc = testCasesSeconds[i];
      const rect = new PdfRect(MARGIN, y, MM(50), MM(30));
      page2.drawString(
        tc.label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + MM(55), y + MM(10), MM(100), MM(5)),
        ETextAlign.CenterLeft
      );
      page2.drawRectangle(rectOutline, null, rect);

      const content = clock.parse(tc.time, {});
      clock.draw(content, rect, ETextAlign.Center, { font, page: page2 });

      y += MM(35);
    }

    const page3 = pdf.addPage();
    page3.drawString(
      'Clock Module Tests - Different Sizes',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const sizeTests = [
      { time: '9:00', size: '30', label: '1. 30mm clock' },
      { time: '9:00', size: '50', label: '2. 50mm clock (default)' },
      { time: '9:00', size: '70', label: '3. 70mm clock' },
      { time: '9:00', size: '100', label: '4. 100mm clock' },
      { time: '15:30:00', size: '40', label: '5. 40mm with seconds' },
      { time: '15:30:00', size: '60', label: '6. 60mm with seconds' },
      { time: '15:30:00', size: '80', label: '7. 80mm with seconds' },
      { time: '15:30:00', size: '120', label: '8. 120mm with seconds' },
    ];

    y = MM(25);
    for (let i = 0; i < sizeTests.length; i++) {
      const tc = sizeTests[i];
      const sizeMm = parseFloat(tc.size);
      const rect = new PdfRect(MARGIN, y, MM(sizeMm), MM(sizeMm));
      page3.drawString(
        tc.label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + MM(sizeMm) + MM(5), y + MM(sizeMm / 2), MM(100), MM(5)),
        ETextAlign.CenterLeft
      );
      page3.drawRectangle(rectOutline, null, rect);

      const content = clock.parse(tc.time, { size: tc.size });
      clock.draw(content, rect, ETextAlign.Center, { font, page: page3 });

      y += MM(sizeMm) + MM(10);
    }

    const page4 = pdf.addPage();
    page4.drawString(
      'Clock Module Tests - Various Times',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const variousTimes = [
      { time: '1:00', label: '1:00' },
      { time: '1:15', label: '1:15' },
      { time: '1:30', label: '1:30' },
      { time: '1:45', label: '1:45' },
      { time: '2:00', label: '2:00' },
      { time: '2:10', label: '2:10' },
      { time: '2:20', label: '2:20' },
      { time: '2:40', label: '2:40' },
      { time: '2:50', label: '2:50' },
      { time: '3:00', label: '3:00' },
      { time: '3:05', label: '3:05' },
      { time: '3:25', label: '3:25' },
      { time: '4:00', label: '4:00' },
      { time: '4:20', label: '4:20' },
      { time: '4:50', label: '4:50' },
      { time: '5:00', label: '5:00' },
    ];

    const clockSize = MM(25);
    const cols = 4;
    const spacing = clockSize + MM(5);
    y = MM(25);

    for (let i = 0; i < variousTimes.length; i++) {
      const tc = variousTimes[i];
      const col = i % cols;
      const row = Math.floor(i / cols);

      const x = MARGIN + col * spacing;
      const rowY = y + row * spacing;

      const rect = new PdfRect(x, rowY, clockSize, clockSize);
      page4.drawRectangle(rectOutline, null, rect);
      page4.drawString(
        tc.label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(x, rowY - MM(4), clockSize, MM(4)),
        ETextAlign.Center
      );

      const content = clock.parse(tc.time, {});
      clock.draw(content, rect, ETextAlign.Center, { font, page: page4 });
    }

    const page5 = pdf.addPage();
    page5.drawString(
      'Clock Module Tests - Size Comparison',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const sizeComparisons = [
      { time: '22:00', size: '100', label: '22:00 - 100mm' },
      { time: '3:00', size: '100', label: '3:00 - 100mm' },
      { time: '8:15', size: '100', label: '8:15 - 100mm' },
      { time: '14:45:30', size: '100', label: '14:45:30 - 100mm' },
    ];

    y = MM(25);
    for (const tc of sizeComparisons) {
      const sizeMm = parseFloat(tc.size);
      const rect = new PdfRect(MARGIN, y, MM(sizeMm), MM(sizeMm));
      page5.drawString(
        tc.label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, y - MM(5), MM(150), MM(5)),
        ETextAlign.CenterLeft
      );
      page5.drawRectangle(rectOutline, null, rect);

      const content = clock.parse(tc.time, { size: tc.size });
      clock.draw(content, rect, ETextAlign.Center, { font, page: page5 });

      y += MM(sizeMm) + MM(15);
    }

    const page6 = pdf.addPage();
    page6.drawString(
      'Clock Module Tests - Empty Clock',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const emptyClockTests = [
      { time: '', size: '50', label: '1. Empty clock - 50mm default' },
      { time: '', size: '30', label: '2. Empty clock - 30mm' },
      { time: '', size: '70', label: '3. Empty clock - 70mm' },
      { time: '', size: '100', label: '4. Empty clock - 100mm' },
    ];

    y = MM(25);
    for (const tc of emptyClockTests) {
      const sizeMm = parseFloat(tc.size);
      const rect = new PdfRect(MARGIN, y, MM(sizeMm), MM(sizeMm));
      page6.drawString(
        tc.label,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN + MM(sizeMm) + MM(5), y + MM(sizeMm / 2), MM(100), MM(5)),
        ETextAlign.CenterLeft
      );
      page6.drawRectangle(rectOutline, null, rect);

      const content = clock.parse(tc.time, { size: tc.size });
      clock.draw(content, rect, ETextAlign.Center, { font, page: page6 });

      y += MM(sizeMm) + MM(15);
    }
  }
}
